import React, {useContext, useState, useEffect, useRef} from 'react';
import PageHeader from "@common/PageHeader";
import {useBreadcrumbComponent} from "@common/useBreadcrumbComponent";
import Button from "@common/Button";
import {AssignmentRounded, CheckRounded, ChevronRightRounded, CloseRounded, DescriptionRounded, DrawRounded, EditRounded, FormatListNumberedRounded, InfoRounded, KeyboardArrowDownRounded, MenuBookRounded, PictureAsPdfRounded, PlayCircleRounded, PushPinRounded, QuizRounded, ReorderRounded, SaveRounded, SearchRounded, SlideshowRounded, SportsEsportsRounded} from "@mui/icons-material";
import {Collapse} from "antd";
import LessonPlanEditor from "./components/LessonPlanEditor";
import SortableSectionList from "./components/SortableSectionList";
import LessonPlanDetailPanel from "./components/LessonPlanDetailPanel";
import MOCK_SECTION_DATA from './mockSectionData';
import {BrandContext} from "@context/BrandContext";
import LessonScheduleNavigator from "./components/LessonScheduleNavigator";
import lessonResourceService from './lessonResourceService';
import CircularLoader from "@common/CircularLoader";
import MathJaxHTMLContent from "@common/MathJaxHTMLContent";
import axios from "@common/axios";

const SECTION_KEYS = ['summary', 'overview', 'textbookContent', 'examples', 'quiz', 'questions', 'quizQuestions', 'walkthroughs', 'extras', 'videos', 'worksheets'];

const initSelections = () => {
    const s = {};
    MOCK_SECTION_DATA.forEach(group =>
        group.items.forEach(item => { s[item.id] = item.topPick; })
    );
    return s;
};

const DUMMY_PLAN = {
    title: '4B · Graphing Straight Lines Using Intercepts',
    subtitle: 'Teacher Lesson Plan · 4 sections',
    overview: {
        description: 'In this lesson, students build on their understanding of linear equations to develop an efficient method for sketching straight-line graphs. Rather than plotting many points, they will discover that just two points are enough to draw any straight line accurately.\n' +
            'The central focus is on two special points — the x-intercept and the y-intercept — and how to find them directly from a linear rule without needing a full table of values.',
        duration: '60 minutes',
        yearLevel: 'Year 9',
        priorKnowledge: 'Basic counting and number sense',
        textbookSections: '4B',
    },
    textbookContent: [
        { code: '4B Lesson Content', title: 'Consider the relation with the y intercept.' },
    ],
    examples: [
        { code: 'Example 4', title: 'Sketching with intercepts', conceptId: 42960, tutorialId: 126951 },
    ],
    quiz: [
        { title: 'Place Value Quick Check', questions: 5, conceptId: 42960, questionClusterId: 33278 },
    ],
    questions: [
        { code: '1A Q1–4', title: 'Identifying digits and place values', testId: 947083 },
    ],
    quizQuestions: [
        { title: 'Level 2 questions', questions: 10, conceptId: 42960, questionLevel: 2 },
    ],
    walkthroughs: [
        { title: 'Walkthrough 1', description: 'Complete a table of values to graph a linear equation.', conceptId: 42960, questionId: 66825838 },
        { title: 'Walkthrough 2', description: <p>Determine the two intercepts to draw a quick sketch of <span style="white-space: nowrap;">a line</span> for an equation in the form <em> <span style="font-family: bookman old style, serif;"> <span style="white-space: nowrap;">y = mx + b</span></span></em><span style="white-space: nowrap;">.</span></p>, conceptId: 42960, questionId: 66836380 },
    ],
    extras: [
        { type: 'scorcher', title: 'A 7-digit number has a 4 in the millions place. What is the smallest such number?' },
        { type: 'desmos', title: 'Place value sliders and visual exploration', conceptId: 13840, mediaId: 184320 },
        { type: 'widget', title: 'Graphing a line', description: 'Watch how to graph a line graph from an equation using the two intercepts and a third check point.', conceptId: 42960, mediaId: 41526 },
    ],
    videos: [
        { title: 'Example 4', description: 'Sketching with intercepts', duration: '4:32', conceptId: 42960, mediaId: 99695 },
    ],
    worksheets: [
        { title: 'Drawing lines from intercepts', description: 'Use the x- and y-intercepts to graph lines.', conceptId: 42960, mediaId: 41315 },
        { title: 'Drawing lines from intercepts SOLUTIONS', description: 'Full solutions for this Worksheet', conceptId: 42960, mediaId: 41316 },
    ],
    summary: [
        'To know that at most two points are required to sketch a straight line graph',
        'To know how to find the x- and y- intercepts from the rule for a linear graph',
        'To be able to sketch a linear graph by finding intercepts and joining in a line',
    ],
};


const MetaGrid = ({ items }) => (
    <div className="ch-lesson-plan-meta-grid">
        {items.map(({ label, value }) => (
            <div key={label} className="ch-lesson-plan-meta-cell">
                <span className="ch-lesson-plan-meta-key">{label}</span>
                <span className="ch-lesson-plan-meta-value">{value}</span>
            </div>
        ))}
    </div>
);

const BulletList = ({ items, renderItem }) => (
    <ul className="ch-lesson-plan-bullet-list">
        {items.map((item, i) => (
            <li key={i} className="ch-lesson-plan-bullet-item">
                {renderItem ? renderItem(item) : item}
            </li>
        ))}
    </ul>
);

const ResourceRow = props => {
    const {type, code, title, onClick, active, onToggleSave, isSaved} = props;
    const className = `ch-lesson-plan-resource-row${onClick ? ' ch-lesson-plan-resource-row--clickable' : ''}${active ? ' ch-lesson-plan-resource-row--active' : ''}`;
    const content = (
        <>
            {type && <span className="ch-lesson-plan-resource-type">{type}</span>}
            {code && <span className="ch-lesson-plan-resource-code">{code}</span>}
            <MathJaxHTMLContent
                ElementTag="span"
                className="ch-lesson-plan-resource-title"
                html={title}
                showLoader={false}
            />
            {onToggleSave && (
                <Button
                    type="text"
                    size="small"
                    icon={<PushPinRounded style={{ fontSize: '16px', color: isSaved ? '#1890ff' : '#ccc' }} />}
                    onClick={(e) => {
                        e.stopPropagation();
                        onToggleSave();
                    }}
                    style={{ marginLeft: 'auto', minWidth: '32px', padding: '4px' }}
                    aria-label={isSaved ? 'Remove from lesson plan' : 'Add to lesson plan'}
                />
            )}
            {onClick && <ChevronRightRounded className="ch-lesson-plan-resource-chevron" fontSize="small" />}
        </>
    );

    if (onClick) {
        return (
            <button
                className={className}
                onClick={onClick}
                aria-expanded={active}
                aria-controls="ch-lesson-plan-detail-panel"
            >
                {content}
            </button>
        );
    }

    return <div className={className}>{content}</div>;
};

const SectionLabel = ({ icon, label }) => (
    <span className="ch-lesson-plan-section-label">
        <span className="ch-lesson-plan-section-icon" aria-hidden="true">{icon}</span>
        {label}
    </span>
);

const sectionExpandIcon = ({ isActive }) => (
    <KeyboardArrowDownRounded
        style={{ fontSize: 18, transform: isActive ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
    />
);

const makeSectionLabel = (icon, text, onEdit, onToggle) => (
    <span className="ch-lesson-plan-section-header-label">
        <button type="button" className="ch-lesson-plan-section-label-btn" onClick={onToggle}>
            <SectionLabel icon={icon} label={text} />
        </button>
        <Button type="text" className="ch-lesson-plan-section-edit-btn" aria-label={`Edit ${text}`} onClick={onEdit}>
            <EditRounded style={{ fontSize: '18px' }} />
        </Button>
    </span>
);

const LessonPlannerLessonPlan = ({ planId }) => {
    const BreadcrumbBar = useBreadcrumbComponent({ dropLast: true });
    const editorRef = useRef(null);
    const [activeItem, setActiveItem] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [isReordering, setIsReordering] = useState(false);
    const [selections, setSelections] = useState(initSelections);
    const [originalSelections, setOriginalSelections] = useState(null);
    const [sectionOrder, setSectionOrder] = useState(SECTION_KEYS);
    const [originalOrder, setOriginalOrder] = useState(null);
    const [openSections, setOpenSections] = useState(new Set(SECTION_KEYS));
    const [editScrollTo, setEditScrollTo] = useState(null);
    const brandConfig = useContext(BrandContext);

    // Resource data fetched from API
    const [loading, setLoading] = useState(true);
    const [lessonPlan, setLessonPlan] = useState(DUMMY_PLAN);
    const [lessonPlanMetadata, setLessonPlanMetadata] = useState(null);
    const [conceptId, setConceptId] = useState(null);
    const [tutorials, setTutorials] = useState([]);
    const [examples, setExamples] = useState([]);
    const [exercises, setExercises] = useState([]);
    const [quizzes, setQuizzes] = useState([]);
    const [walkthroughs, setWalkthroughs] = useState([]);
    const [videos, setVideos] = useState([]);
    const [worksheets, setWorksheets] = useState([]);
    const [extras, setExtras] = useState([]);
    const [savedResources, setSavedResources] = useState(new Set());

    // Fetch lesson plan metadata to get conceptId and lessonName
    useEffect(() => {
        if (!planId) return;

        axios.get(`/web/ui/lessonPlanner/lessonPlans/${planId}`)
            .then(response => {
                const dto = response.data;
                console.log('Loaded lesson plan DTO:', dto);

                // Store metadata (lessonName, etc.)
                setLessonPlanMetadata(dto);

                // Extract conceptId - use the first concept in the concepts array or fallback to single concept
                const extractedConceptId = dto.concepts?.[0]?.conceptId || dto.conceptId;
                if (extractedConceptId) {
                    setConceptId(extractedConceptId);
                } else {
                    console.warn('No conceptId found in lesson plan DTO');
                }
            })
            .catch(error => {
                console.error('Error loading lesson plan:', error);
            });
    }, [planId]);

    // Fetch saved resources for this lesson plan
    useEffect(() => {
        if (!planId) return;

        axios.get(`/web/ui/lessonPlanner/lessonPlans/${planId}/resources`)
            .then(response => {
                const resources = response.data;
                console.log('Loaded saved resources:', resources);

                // Create a Set of saved resource keys for quick lookup
                // Key format: "resourceType:resourceId"
                const savedSet = new Set(
                    resources.map(r => `${r.resourceType}:${r.resourceId}`)
                );
                setSavedResources(savedSet);
            })
            .catch(error => {
                console.error('Error loading saved resources:', error);
            });
    }, [planId]);

    // Helper to determine resource type and section key
    const getResourceTypeAndKey = (resourceItem, sectionKey) => {
        if (resourceItem.tutorialId) return { resourceType: 'TUTORIAL', resourceId: resourceItem.tutorialId, sectionKey };
        if (resourceItem.questionClusterId) return { resourceType: 'QUESTION_CLUSTER', resourceId: resourceItem.questionClusterId, sectionKey };
        if (resourceItem.questionId) return { resourceType: 'QUESTION', resourceId: resourceItem.questionId, sectionKey };
        if (resourceItem.mediaId) return { resourceType: 'MEDIA', resourceId: resourceItem.mediaId, sectionKey };
        return null;
    };

    // Toggle resource saved state
    const toggleResourceSaved = (resourceItem, sectionKey) => {
        const resourceInfo = getResourceTypeAndKey(resourceItem, sectionKey);
        if (!resourceInfo) {
            console.error('Could not determine resource type for:', resourceItem);
            return;
        }

        const key = `${resourceInfo.resourceType}:${resourceInfo.resourceId}`;
        const isSaved = savedResources.has(key);

        if (isSaved) {
            // Remove resource
            // Find the lessonPlanResourceId from the backend
            axios.get(`/web/ui/lessonPlanner/lessonPlans/${planId}/resources`)
                .then(response => {
                    const resource = response.data.find(r =>
                        r.resourceType === resourceInfo.resourceType &&
                        r.resourceId === resourceInfo.resourceId
                    );

                    if (resource) {
                        return axios.delete(`/web/ui/lessonPlanner/lessonPlans/${planId}/resources/${resource.lessonPlanResourceId}`);
                    }
                })
                .then(() => {
                    setSavedResources(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(key);
                        return newSet;
                    });
                    displayToastMessage('Resource removed', 'Resource removed from lesson plan', 'success');
                })
                .catch(error => {
                    console.error('Error removing resource:', error);
                    displayToastMessage('Remove failed', 'Failed to remove resource', 'error');
                });
        } else {
            // Add resource
            const payload = {
                resourceType: resourceInfo.resourceType,
                resourceId: resourceInfo.resourceId,
                sectionKey: resourceInfo.sectionKey,
                selected: true,
                topPick: false,
            };

            axios.post(`/web/ui/lessonPlanner/lessonPlans/${planId}/resources`, payload)
                .then(() => {
                    setSavedResources(prev => new Set([...prev, key]));
                    displayToastMessage('Resource added', 'Resource added to lesson plan', 'success');
                })
                .catch(error => {
                    console.error('Error adding resource:', error);
                    displayToastMessage('Add failed', 'Failed to add resource', 'error');
                });
        }
    };

    // Helper to check if resource is saved
    const isResourceSaved = (resourceItem) => {
        const resourceInfo = getResourceTypeAndKey(resourceItem, null);
        if (!resourceInfo) return false;
        const key = `${resourceInfo.resourceType}:${resourceInfo.resourceId}`;
        return savedResources.has(key);
    };

    // Fetch resources when conceptId changes
    useEffect(() => {
        if (!conceptId) return;

        const fetchResources = async () => {
            setLoading(true);
            try {
                // Fetch all resources in parallel
                const [
                    tutorialsPage,
                    examplesPage,
                    exercisesPage,
                    quizzesPage,
                    walkthroughsPage,
                    videosPage,
                    worksheetsPage,
                    desmosPage,
                    widgetsPage,
                    scorcherPage,
                ] = await Promise.all([
                    lessonResourceService.getTutorials(conceptId, 'LESSON', null, 0, 100),
                    lessonResourceService.getTutorials(conceptId, 'GENERAL_CONTENT', ['EXAMPLE'], 0, 100),
                    lessonResourceService.getQuestionClusters(conceptId, ['EXERCISE'], null, 0, 100),
                    lessonResourceService.getQuestionClusters(conceptId, ['QUICK_QUIZ'], null, 0, 100),
                    lessonResourceService.getWalkthroughQuestions(conceptId, 0, 100),
                    lessonResourceService.getMediaResources(conceptId, 'VIDEO', 0, 100),
                    lessonResourceService.getMediaResources(conceptId, 'WORKSHEET', 0, 100),
                    lessonResourceService.getMediaResources(conceptId, 'DESMOS', 0, 50),
                    lessonResourceService.getMediaResources(conceptId, 'WIDGET', 0, 50),
                    lessonResourceService.getMediaResources(conceptId, 'SCORCHER', 0, 50),
                ]);

                // Transform API data to component format
                setTutorials(tutorialsPage.content.map(t => ({
                    code: t.tutorialNumber ? `Tutorial ${t.tutorialNumber}` : '',
                    title: t.tutorialName,
                    conceptId,
                    tutorialId: t.tutorialId,
                })));

                setExamples(examplesPage.content.map(t => ({
                    code: t.tutorialNumber ? `Example ${t.tutorialNumber}` : '',
                    title: t.tutorialName,
                    conceptId,
                    tutorialId: t.tutorialId,
                })));

                setExercises(exercisesPage.content.map(c => ({
                    title: c.name,
                    questions: c.questionCount,
                    conceptId,
                    questionClusterId: c.questionClusterId,
                })));

                setQuizzes(quizzesPage.content.map(c => ({
                    title: c.name,
                    questions: c.questionCount,
                    conceptId,
                    questionClusterId: c.questionClusterId,
                })));

                setWalkthroughs(walkthroughsPage.content.map(q => ({
                    title: q.instructions || 'Walkthrough',
                    description: q.contentSummary || '',
                    conceptId,
                    questionId: q.questionId,
                })));

                setVideos(videosPage.content.map(m => ({
                    title: m.mediaName,
                    description: m.contentSummary || m.mediaDescription || '',
                    duration: '', // Duration not available in MediaBean
                    conceptId,
                    mediaId: m.mediaId,
                })));

                setWorksheets(worksheetsPage.content.map(m => ({
                    title: m.mediaName,
                    description: m.contentSummary || m.mediaDescription || '',
                    conceptId,
                    mediaId: m.mediaId,
                })));

                // Combine extras (Desmos, Widgets, Scorchers)
                const extrasData = [
                    ...desmosPage.content.map(m => ({
                        type: 'desmos',
                        title: m.mediaName,
                        description: m.contentSummary || m.mediaDescription || '',
                        conceptId,
                        mediaId: m.mediaId,
                    })),
                    ...widgetsPage.content.map(m => ({
                        type: 'widget',
                        title: m.mediaName,
                        description: m.contentSummary || m.mediaDescription || '',
                        conceptId,
                        mediaId: m.mediaId,
                    })),
                    ...scorcherPage.content.map(m => ({
                        type: 'scorcher',
                        title: m.mediaName,
                        description: m.contentSummary || m.mediaDescription || '',
                        conceptId,
                        mediaId: m.mediaId,
                    })),
                ];
                setExtras(extrasData);

                setLoading(false);
            } catch (error) {
                console.error('Error fetching lesson resources:', error);
                setLoading(false);
            }
        };

        fetchResources();
    }, [conceptId]);

    const openItem = (item) => setActiveItem(item);
    const closePanel = () => setActiveItem(null);

    const enterEdit = () => {
        setOriginalSelections({...selections});
        setIsEditing(true);
        setActiveItem(null);
    };

    const editSection = (key) => {
        enterEdit();
        setEditScrollTo(key);
    };

    const saveEdit = () => {
        if (!planId) {
            console.error('No planId available for saving');
            return;
        }

        if (!editorRef.current) {
            console.error('Editor ref not available');
            return;
        }

        // Call the editor's save method (which calls PUT /resources/bulk)
        editorRef.current.save()
            .then(() => {
                // Reload saved resources to update the view
                return axios.get(`/web/ui/lessonPlanner/lessonPlans/${planId}/resources`);
            })
            .then(response => {
                const resources = response.data;
                const savedSet = new Set(
                    resources.map(r => `${r.resourceType}:${r.resourceId}`)
                );
                setSavedResources(savedSet);

                displayToastMessage('Saved', 'Lesson plan resources saved successfully', 'success');
                setIsEditing(false);
                setOriginalSelections(null);
                setEditScrollTo(null);
            })
            .catch(error => {
                console.error('Error saving lesson plan resources:', error);
                displayToastMessage('Save failed', error.response?.data?.message || 'Failed to save resources', 'error');
            });
    };

    const cancelEdit = () => {
        setSelections(originalSelections);
        setIsEditing(false);
        setOriginalSelections(null);
        setEditScrollTo(null);
    };

    const toggleSection = (sectionKey, keys) => {
        setOpenSections(prev => {
            const next = new Set(prev);
            if (keys === undefined) {
                if (next.has(sectionKey)) next.delete(sectionKey);
                else next.add(sectionKey);
            } else if (Array.isArray(keys) && keys.length > 0) {
                next.add(sectionKey);
            } else {
                next.delete(sectionKey);
            }
            return next;
        });
    };

    const enterReorder = () => {
        setOriginalOrder([...sectionOrder]);
        setIsReordering(true);
    };

    const saveReorder = () => {
        setIsReordering(false);
        setOriginalOrder(null);
    };

    const cancelReorder = () => {
        setSectionOrder(originalOrder);
        setIsReordering(false);
        setOriginalOrder(null);
    };

    const SECTIONS = [
        {
            key: 'summary',
            label: makeSectionLabel(<PushPinRounded />, 'Lesson Summary', () => editSection('summary'), () => toggleSection('summary')),
            children: (
                <div className="ch-lesson-plan-summary">
                    {lessonPlanMetadata?.concepts?.map((concept, index) => (
                        <div key={concept.conceptId} style={{ marginBottom: index < lessonPlanMetadata.concepts.length - 1 ? '20px' : '0' }}>
                            <h4 style={{ marginBottom: '8px', fontSize: '16px', fontWeight: '600' }}>{concept.conceptName}</h4>
                            <p style={{ margin: 0, lineHeight: '1.6' }}>{concept.contentSummary}</p>
                        </div>
                    )) || <BulletList items={DUMMY_PLAN.summary} />}
                </div>
            ),
        },
        {
            key: 'overview',
            label: makeSectionLabel(<InfoRounded />, 'Lesson Overview', () => editSection('overview'), () => toggleSection('overview')),
            children: (
                <div className="ch-lesson-plan-overview">
                    <p className="ch-lesson-plan-overview-description">{DUMMY_PLAN.overview.description}</p>
                    <MetaGrid items={[
                        { label: 'Estimated Duration',  value: DUMMY_PLAN.overview.duration },
                        { label: 'Year Level',          value: DUMMY_PLAN.overview.yearLevel },
                        { label: 'Prior Knowledge',     value: DUMMY_PLAN.overview.priorKnowledge },
                        { label: 'Textbook Sections',   value: DUMMY_PLAN.overview.textbookSections },
                        { label: 'Syllabus Coverage',   value: 'AC9M7N01, AC9M7N02, AC9M7N03' },
                    ]} />
                </div>
            ),
        },
        {
            key: 'textbookContent',
            label: makeSectionLabel(<MenuBookRounded />, 'Textbook Section Content', () => editSection('textbookContent'), () => toggleSection('textbookContent')),
            children: loading ? (
                <div style={{ padding: '20px', textAlign: 'center' }}><CircularLoader size="small" /></div>
            ) : (
                <div>
                    {tutorials.length > 0 ? tutorials.map((ex, i) => (
                        <ResourceRow
                            key={i}
                            code={ex.code}
                            title={ex.title}
                            onToggleSave={() => toggleResourceSaved(ex, 'textbookContent')}
                            isSaved={isResourceSaved(ex)}
                        />
                    )) : <div style={{ padding: '12px', color: '#999' }}>No textbook content available</div>}
                </div>
            ),
        },
        {
            key: 'examples',
            label: makeSectionLabel(<DrawRounded />, 'Examples', () => editSection('examples'), () => toggleSection('examples')),
            children: loading ? (
                <div style={{ padding: '20px', textAlign: 'center' }}><CircularLoader size="small" /></div>
            ) : (
                <div>
                    {examples.length > 0 ? examples.map((ex, i) => (
                        <ResourceRow
                            key={i}
                            code={ex.code}
                            title={ex.title}
                            active={activeItem?.type === 'examples' && activeItem?.tutorialId === ex.tutorialId && activeItem?.title === ex.title}
                            onClick={() => openItem({ ...ex, type: 'examples' })}
                            onToggleSave={() => toggleResourceSaved(ex, 'examples')}
                            isSaved={isResourceSaved(ex)}
                        />
                    )) : <div style={{ padding: '12px', color: '#999' }}>No examples available</div>}
                </div>
            ),
        },
        {
            key: 'questions',
            label: makeSectionLabel(<QuizRounded />, 'Questions', () => editSection('questions'), () => toggleSection('questions')),
            children: (
                <div>
                    {DUMMY_PLAN.questions.map((q, i) => (
                        <ResourceRow
                            key={i}
                            code={q.code}
                            title={q.title}
                            testId={q.testId}
                            onClick={() => openItem({ ...q, type: 'questions' })}
                        />
                    ))}
                </div>
            ),
        },
        {
            key: 'quiz',
            label: makeSectionLabel(<AssignmentRounded />, brandConfig.exercisesName, () => editSection('quiz'), () => toggleSection('quiz')),
            children: loading ? (
                <div style={{ padding: '20px', textAlign: 'center' }}><CircularLoader size="small" /></div>
            ) : (
                <div>
                    {exercises.length > 0 ? exercises.map((e, i) => (
                        <ResourceRow
                            key={i}
                            title={`${e.title} — ${e.questions} questions`}
                            active={activeItem?.type === 'exercise' && activeItem?.questionClusterId === e.questionClusterId}
                            onClick={() => openItem({ ...e, type: 'exercise' })}
                            onToggleSave={() => toggleResourceSaved(e, 'quiz')}
                            isSaved={isResourceSaved(e)}
                        />
                    )) : <div style={{ padding: '12px', color: '#999' }}>No exercises available</div>}
                </div>
            ),
        },
        {
            key: 'quizQuestions',
            label: makeSectionLabel(<QuizRounded />, 'Quizzes', () => editSection('quizQuestions'), () => toggleSection('quizQuestions')),
            children: loading ? (
                <div style={{ padding: '20px', textAlign: 'center' }}><CircularLoader size="small" /></div>
            ) : (
                <div>
                    {quizzes.length > 0 ? quizzes.map((q, i) => (
                        <ResourceRow
                            key={i}
                            title={`${q.title} — ${q.questions} questions`}
                            active={activeItem?.type === 'quiz' && activeItem?.questionClusterId === q.questionClusterId}
                            onClick={() => openItem({ ...q, type: 'quiz' })}
                            onToggleSave={() => toggleResourceSaved(q, 'quizQuestions')}
                            isSaved={isResourceSaved(q)}
                        />
                    )) : <div style={{ padding: '12px', color: '#999' }}>No quizzes available</div>}
                </div>
            ),
        },
        {
            key: 'walkthroughs',
            label: makeSectionLabel(<SearchRounded />, brandConfig.walkthroughsName, () => editSection('walkthroughs'), () => toggleSection('walkthroughs')),
            children: loading ? (
                <div style={{ padding: '20px', textAlign: 'center' }}><CircularLoader size="small" /></div>
            ) : (
                <div>
                    {walkthroughs.length > 0 ? walkthroughs.map((w, i) => (
                        <ResourceRow
                            key={i}
                            title={w.title}
                            active={activeItem?.type === 'walkthrough' && activeItem?.questionId === w.questionId && activeItem?.title === w.title}
                            onClick={() => openItem({ ...w, type: 'walkthrough' })}
                            onToggleSave={() => toggleResourceSaved(w, 'walkthroughs')}
                            isSaved={isResourceSaved(w)}
                        />
                    )) : <div style={{ padding: '12px', color: '#999' }}>No walkthroughs available</div>}
                </div>
            ),
        },
        {
            key: 'extras',
            label: makeSectionLabel(<SportsEsportsRounded />, `${brandConfig.scorcherName}, ${brandConfig.desmosName} and ${brandConfig.widgetsName}`, () => editSection('extras'), () => toggleSection('extras')),
            children: loading ? (
                <div style={{ padding: '20px', textAlign: 'center' }}><CircularLoader size="small" /></div>
            ) : (
                <div>
                    {extras.length > 0 ? extras.map((e, i) => (
                        <ResourceRow
                            key={i}
                            type={e.type}
                            title={e.title}
                            active={(activeItem?.type === 'scorcher' || activeItem?.type === 'desmos' || activeItem?.type === 'widget') && activeItem?.mediaId === e.mediaId}
                            onClick={() => openItem({ ...e })}
                            onToggleSave={() => toggleResourceSaved(e, 'extras')}
                            isSaved={isResourceSaved(e)}
                        />
                    )) : <div style={{ padding: '12px', color: '#999' }}>No extras available</div>}
                </div>
            ),
        },
        {
            key: 'videos',
            label: makeSectionLabel(<PlayCircleRounded />, brandConfig.videosName, () => editSection('videos'), () => toggleSection('videos')),
            children: loading ? (
                <div style={{ padding: '20px', textAlign: 'center' }}><CircularLoader size="small" /></div>
            ) : (
                <div>
                    {videos.length > 0 ? videos.map((v, i) => (
                        <ResourceRow
                            key={i}
                            code={v.duration}
                            title={v.title}
                            active={activeItem?.type === 'video' && activeItem?.mediaId === v.mediaId && activeItem?.title === v.title}
                            onClick={() => openItem({ ...v, type: 'video' })}
                            onToggleSave={() => toggleResourceSaved(v, 'videos')}
                            isSaved={isResourceSaved(v)}
                        />
                    )) : <div style={{ padding: '12px', color: '#999' }}>No videos available</div>}
                </div>
            ),
        },
        {
            key: 'worksheets',
            label: makeSectionLabel(<DescriptionRounded />, brandConfig.worksheetsName, () => editSection('worksheets'), () => toggleSection('worksheets')),
            children: loading ? (
                <div style={{ padding: '20px', textAlign: 'center' }}><CircularLoader size="small" /></div>
            ) : (
                <div>
                    {worksheets.length > 0 ? worksheets.map((w, i) => (
                        <ResourceRow
                            key={i}
                            title={w.title}
                            active={activeItem?.type === 'worksheet' && activeItem?.mediaId === w.mediaId && activeItem?.title === w.title}
                            onClick={() => openItem({ ...w, type: 'worksheet' })}
                            onToggleSave={() => toggleResourceSaved(w, 'worksheets')}
                            isSaved={isResourceSaved(w)}
                        />
                    )) : <div style={{ padding: '12px', color: '#999' }}>No worksheets available</div>}
                </div>
            ),
        },
    ];

    return (
        <div>
            <BreadcrumbBar />
            <PageHeader
                title={lessonPlanMetadata?.lessonName || DUMMY_PLAN.title}
                subtitle={DUMMY_PLAN.subtitle}
                sticky={true}
                buttons={
                    <>
                        {isEditing ? (
                            isReordering ? (
                                <>
                                    <Button type={'outline'} icon={<CloseRounded />} onClick={cancelReorder}>Cancel</Button>
                                    <Button type={'primary'} icon={<CheckRounded />} onClick={saveReorder}>Finished</Button>
                                </>
                            ) : (
                                <>
                                    <Button type={'outline'} icon={<ReorderRounded />} onClick={enterReorder}>Reorder</Button>
                                    <Button type={'outline'} icon={<CloseRounded />} onClick={cancelEdit}>Cancel</Button>
                                    <Button type={'primary'} icon={<SaveRounded />} onClick={saveEdit}>Save</Button>
                                </>
                            )
                        ) : (
                            <>
                                <Button type={'outline'} icon={<EditRounded />} onClick={enterEdit}>Edit</Button>
                                <Button type={'outline'} icon={<PictureAsPdfRounded />}>Export</Button>
                                <Button type={'outline'} icon={<SlideshowRounded />}>Generate PowerPoint</Button>
                            </>
                        )}
                    </>
                }
            />
            {String(planId) === '1' && <LessonScheduleNavigator />}
            <div className="ch-lesson-plan-content-area">
                <div className="ch-lesson-plan-doc">
                    <div className="ch-lesson-plan-sections">
                        {isEditing && isReordering ? (
                            <SortableSectionList sections={SECTIONS} sectionOrder={sectionOrder} onOrderChange={setSectionOrder} />
                        ) : isEditing ? (
                            <LessonPlanEditor ref={editorRef} lessonPlanId={planId} conceptId={conceptId} scrollTo={editScrollTo} />
                        ) : (
                            sectionOrder.map(key => {
                                const section = SECTIONS.find(s => s.key === key);
                                return (
                                    <Collapse
                                        key={section.key}
                                        items={[section]}
                                        activeKey={openSections.has(section.key) ? [section.key] : []}
                                        onChange={(keys) => toggleSection(section.key, keys)}
                                        ghost
                                        expandIconPlacement="end"
                                        collapsible="icon"
                                        expandIcon={sectionExpandIcon}
                                    />
                                );
                            })
                        )}
                    </div>
                </div>
                <LessonPlanDetailPanel activeItem={activeItem} onClose={closePanel} />
            </div>
        </div>
    );
};

export default LessonPlannerLessonPlan;
